import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  Search,
  Download,
  Mail,
  MessageSquare,
  Phone,
  Send,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  RefreshCw,
  Calendar } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const channelIcons = {
  email: Mail,
  whatsapp: MessageSquare,
  sms: Phone,
  telegram: Send
};

const statusColors = {
  delivered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50',
  read: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/50',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50'
};

export const DeliveryLogs = () => {const { t } = useTranslation("comms");
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/communications/delivery-logs`;
      if (activeTab !== 'all') {
        url += `?channel=${activeTab}`;
      }
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch logs');
      const data = await response.json();
      setLogs(data);
    } catch (err) {
      toast.error('Failed to load delivery logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [activeTab]);

  const filteredLogs = logs.filter((log) => {
    if (!searchTerm) return true;
    return log.template?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.recipientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.recipient?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const stats = {
    total: logs.length,
    delivered: logs.filter((l) => l.status === 'delivered' || l.status === 'read').length,
    failed: logs.filter((l) => l.status === 'failed').length,
    totalCost: logs.reduce((sum, l) => sum + (l.cost || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="communications-logs">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("deliveryLogs", "Delivery Logs")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("trackMessageDelivery", "Track message delivery status and history")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchLogs} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("export", "Export")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated border-l-4 border-l-violet-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("totalMessages", "Total Messages")}</p>
            <p className="text-2xl font-bold">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("delivered", "Delivered")}</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.delivered}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("failed", "Failed")}</p>
            <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("totalCost", "Total Cost")}</p>
            <p className="text-2xl font-bold">${stats.totalCost.toFixed(4)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by template, recipient..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">{t("all", "All")}</TabsTrigger>
                <TabsTrigger value="email">{t("email", "Email")}</TabsTrigger>
                <TabsTrigger value="whatsapp">{t("whatsapp", "WhatsApp")}</TabsTrigger>
                <TabsTrigger value="sms">{t("sms", "SMS")}</TabsTrigger>
                <TabsTrigger value="telegram">{t("telegram", "Telegram")}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="card-elevated">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("channel", "Channel")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("template", "Template")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("recipient", "Recipient")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("module", "Module")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("sentAt", "Sent At")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("cost", "Cost")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log) => {
                  const Icon = channelIcons[log.channel] || Mail;
                  return (
                    <tr key={log.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-3 px-4">
                        <div className={`inline-flex items-center gap-2 p-2 rounded-lg ${
                        log.channel === 'email' ? 'bg-violet-100 dark:bg-violet-900/30' :
                        log.channel === 'whatsapp' ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                        log.channel === 'sms' ? 'bg-orange-100 dark:bg-orange-900/30' :
                        'bg-blue-100 dark:bg-blue-900/30'}`
                        }>
                          <Icon className={`h-4 w-4 ${
                          log.channel === 'email' ? 'text-violet-600' :
                          log.channel === 'whatsapp' ? 'text-emerald-600' :
                          log.channel === 'sms' ? 'text-orange-600' :
                          'text-blue-600'}`
                          } />
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <p className="font-medium text-sm">{log.template}</p>
                        <p className="text-xs text-muted-foreground font-mono">{log.messageId}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm font-medium">{log.recipientName}</p>
                        <p className="text-xs text-muted-foreground">{log.recipient}</p>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="secondary" className="capitalize">{log.module}</Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5 text-sm">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          {new Date(log.sentAt).toLocaleString()}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className={statusColors[log.status] || statusColors.pending}>
                          {log.status === 'delivered' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                          {log.status === 'read' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                          {log.status === 'failed' && <XCircle className="h-3 w-3 mr-1" />}
                          {log.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                          {log.status}
                        </Badge>
                        {log.error &&
                        <p className="text-xs text-red-600 mt-1">{log.error}</p>
                        }
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm font-mono">
                          {log.cost > 0 ? `$${(log.cost ?? 0).toFixed(4)}` : 'Free'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Eye className="h-4 w-4" />
                        </Button>
                        {log.status === 'failed' &&
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600">
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        }
                      </td>
                    </tr>);

                })}
              </tbody>
            </table>
          </div>

          {filteredLogs.length === 0 &&
          <div className="py-12 text-center">
              <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noLogsFound", "No logs found")}</h3>
              <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
            </div>
          }
        </CardContent>
      </Card>
    </div>);

};